import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowDownTrayIcon,
  LockClosedIcon,
  DocumentIcon,
  CloudArrowDownIcon,
  DocumentTextIcon,
  FolderIcon,
  ChevronRightIcon,
  HomeIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { publicPortalApi } from '../api/clients';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { formatBytes, formatShortDate } from '../utils/dateFormat';
import { generateProfessionalPdf, detectDocumentType } from '../components/documents/PdfExporter';
import { projectTypeLabels, projectStatusLabels } from '@affogato/shared';
import type { PublicPortalFile, PublicPortalProject, PublicPortalFolder, ProjectType, ProjectStatus } from '@affogato/shared';
import clsx from 'clsx';

// View modes for navigation
type ViewMode = 'overview' | 'project' | 'folder';

// Breadcrumb item type
interface BreadcrumbItem {
  id: string | null;
  name: string;
  type: 'home' | 'project' | 'folder';
}

// Status colors for project badges
const statusColors: Record<ProjectStatus, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  paused: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

// Type colors for project icons
const typeColors: Record<ProjectType, string> = {
  fiscal: '#10b981',
  audit: '#6366f1',
  payroll: '#f59e0b',
  consulting: '#8b5cf6',
  legal: '#ef4444',
  accounting: '#3b82f6',
  other: '#6b7280',
};

/**
 * Simple markdown to HTML renderer for basic formatting
 */
function renderMarkdown(content: string): string {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h4 class="text-md font-semibold mt-4 mb-2 text-gray-900 dark:text-white">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="text-lg font-semibold mt-4 mb-2 text-gray-900 dark:text-white">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-white">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li class="ml-4 text-gray-700 dark:text-slate-300">$1</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, '<ul class="list-disc mb-3 pl-4">$&</ul>')
    .replace(/\n\n/g, '</p><p class="mb-3 text-gray-700 dark:text-slate-300">')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p class="mb-3 text-gray-700 dark:text-slate-300">')
    .replace(/$/, '</p>');
}

export function PublicPortalPage() {
  const { t } = useTranslation('public');
  const { token } = useParams<{ token: string }>();

  // Portal info state
  const [portalInfo, setPortalInfo] = useState<{
    clientName: string;
    companyName?: string;
    hasPassword: boolean;
    brandingConfig?: { logo?: string; primaryColor?: string; companyName?: string } | null;
  } | null>(null);

  // Authentication state
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [password, setPassword] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState<string | undefined>();
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Legacy files state (individual shared files)
  const [files, setFiles] = useState<PublicPortalFile[]>([]);
  const [report, setReport] = useState<{
    id: string;
    title: string;
    content: string;
    publishedAt: string;
  } | null>(null);

  // Project-based navigation state
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [projects, setProjects] = useState<PublicPortalProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<PublicPortalProject | null>(null);
  const [folders, setFolders] = useState<PublicPortalFolder[]>([]);
  const [_currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderFiles, setFolderFiles] = useState<PublicPortalFile[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  // Download state
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      loadPortalInfo();
    }
  }, [token]);

  const loadPortalInfo = async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      const response = await publicPortalApi.getInfo(token);
      setPortalInfo(response.portal);

      // If no password required, load content immediately
      if (!response.portal.hasPassword) {
        setIsVerified(true);
        await loadContent();
      }
    } catch (err: any) {
      setError(err.message || t('portal.notFound'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadContent = async (pwd?: string) => {
    if (!token) return;

    try {
      // Load legacy files, report, and projects in parallel
      const [filesResponse, reportResponse, projectsResponse] = await Promise.all([
        publicPortalApi.getFiles(token, pwd),
        publicPortalApi.getReport(token, pwd).catch(() => ({ report: null })),
        publicPortalApi.getProjects(token, pwd).catch(() => ({ projects: [] })),
      ]);
      setFiles(filesResponse.files);
      setReport(reportResponse.report);
      setProjects(projectsResponse.projects);
    } catch (err: any) {
      if (err.status === 401) {
        setError(t('portal.passwordRequired'));
        setIsVerified(false);
      } else {
        setError(err.message || t('portal.notFound'));
      }
    }
  };

  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !password) return;

    setIsVerifying(true);
    setError('');

    try {
      await publicPortalApi.verifyPassword(token, password);
      setIsVerified(true);
      setVerifiedPassword(password);
      await loadContent(password);
    } catch (err: any) {
      if (err.status === 401) {
        setError(t('portal.incorrectPassword'));
      } else {
        setError(err.message || t('portal.verificationFailed'));
      }
    } finally {
      setIsVerifying(false);
    }
  };

  // Check if file is markdown
  const isMarkdownFile = (fileName: string) => /\.md$/i.test(fileName);

  // Download markdown as PDF
  const downloadMarkdownAsPdf = async (downloadUrl: string, fileName: string) => {
    const response = await fetch(downloadUrl);
    const markdown = await response.text();
    const locale = navigator.language?.startsWith('es') ? 'es' : 'en';
    await generateProfessionalPdf({
      title: fileName,
      markdown,
      documentType: detectDocumentType(fileName),
      locale,
    });
  };

  // Download legacy file
  const handleDownloadFile = async (file: PublicPortalFile) => {
    if (!token) return;

    setDownloadingFileId(file.id);
    try {
      const response = await publicPortalApi.downloadFile(token, file.id, verifiedPassword);
      if (isMarkdownFile(file.name)) {
        await downloadMarkdownAsPdf(response.downloadUrl, response.fileName);
      } else {
        triggerDownload(response.downloadUrl, response.fileName);
      }
    } catch (err: any) {
      setError(err.message || t('portal.downloadFailed'));
    } finally {
      setDownloadingFileId(null);
    }
  };

  // Download project file
  const handleDownloadProjectFile = async (file: PublicPortalFile) => {
    if (!token || !selectedProject) return;

    setDownloadingFileId(file.id);
    try {
      const response = await publicPortalApi.downloadProjectFile(
        token,
        selectedProject.id,
        file.id,
        verifiedPassword
      );
      if (isMarkdownFile(file.name)) {
        await downloadMarkdownAsPdf(response.downloadUrl, response.fileName);
      } else {
        triggerDownload(response.downloadUrl, response.fileName);
      }
    } catch (err: any) {
      setError(err.message || t('portal.downloadFailed'));
    } finally {
      setDownloadingFileId(null);
    }
  };

  const triggerDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Navigate to a project
  const handleProjectClick = async (project: PublicPortalProject) => {
    if (!token) return;

    setIsLoadingContent(true);
    setSelectedProject(project);
    setViewMode('project');
    setBreadcrumb([
      { id: null, name: 'Inicio', type: 'home' },
      { id: project.id, name: project.name, type: 'project' },
    ]);

    try {
      const response = await publicPortalApi.getProjectFolders(token, project.id, null, verifiedPassword);
      setFolders(response.folders);
      setCurrentFolderId(null);
      setFolderFiles([]);
    } catch (err: any) {
      setError(err.message || 'Error al cargar carpetas');
    } finally {
      setIsLoadingContent(false);
    }
  };

  // Navigate to a folder
  const handleFolderClick = async (folder: PublicPortalFolder) => {
    if (!token || !selectedProject) return;

    setIsLoadingContent(true);
    setViewMode('folder');
    setCurrentFolderId(folder.id);

    // Update breadcrumb
    const existingIndex = breadcrumb.findIndex((b) => b.id === folder.id);
    if (existingIndex >= 0) {
      // Going back to an existing folder in breadcrumb
      setBreadcrumb(breadcrumb.slice(0, existingIndex + 1));
    } else {
      // Going deeper
      setBreadcrumb([...breadcrumb, { id: folder.id, name: folder.name, type: 'folder' }]);
    }

    try {
      // Load subfolders and files in parallel
      const [foldersResponse, filesResponse] = await Promise.all([
        publicPortalApi.getProjectFolders(token, selectedProject.id, folder.id, verifiedPassword),
        publicPortalApi.getProjectFiles(token, selectedProject.id, folder.id, verifiedPassword),
      ]);
      setFolders(foldersResponse.folders);
      setFolderFiles(filesResponse.files);
    } catch (err: any) {
      setError(err.message || 'Error al cargar contenido');
    } finally {
      setIsLoadingContent(false);
    }
  };

  // Navigate via breadcrumb
  const handleBreadcrumbClick = async (item: BreadcrumbItem) => {
    if (item.type === 'home') {
      setViewMode('overview');
      setSelectedProject(null);
      setCurrentFolderId(null);
      setFolders([]);
      setFolderFiles([]);
      setBreadcrumb([]);
    } else if (item.type === 'project' && selectedProject) {
      await handleProjectClick(selectedProject);
    } else if (item.type === 'folder' && item.id) {
      const folder = { id: item.id, name: item.name, parentId: null };
      await handleFolderClick(folder);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state (no portal found)
  if (error && !portalInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('portal.notFound')}</h1>
          <p className="text-gray-500 dark:text-slate-400">{error}</p>
        </motion.div>
      </div>
    );
  }

  // Password verification screen
  if (portalInfo?.hasPassword && !isVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 max-w-md w-full"
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg mb-4">
              <LockClosedIcon className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('portal.protectedPortal')}</h1>
            <p className="text-gray-500 dark:text-slate-400 mt-1">
              {portalInfo.companyName && `${portalInfo.companyName} • `}
              {portalInfo.clientName}
            </p>
          </div>

          <form onSubmit={handleVerifyPassword} className="space-y-4">
            <Input
              type="password"
              label={t('portal.password')}
              placeholder={t('portal.enterPassword')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={error}
              leftIcon={<LockClosedIcon className="w-5 h-5" />}
              autoFocus
            />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              isLoading={isVerifying}
            >
              {t('portal.accessPortal')}
            </Button>
          </form>

          <p className="text-center text-xs text-gray-400 dark:text-slate-500 mt-6">
            {t('portal.poweredBy')}
          </p>
        </motion.div>
      </div>
    );
  }

  // Main portal view
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg mb-4">
            <CloudArrowDownIcon className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {portalInfo?.companyName || t('portal.filePortal')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">
            {t('portal.filesFor', { name: portalInfo?.clientName })}
          </p>
        </div>

        {/* Breadcrumb Navigation */}
        {viewMode !== 'overview' && (
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl shadow border border-white/20 dark:border-slate-700/50 px-4 py-2 mb-4 flex items-center gap-2 overflow-x-auto">
            {breadcrumb.map((item, index) => (
              <div key={item.id || 'home'} className="flex items-center gap-2 flex-shrink-0">
                {index > 0 && <ChevronRightIcon className="w-4 h-4 text-gray-400" />}
                <button
                  onClick={() => handleBreadcrumbClick(item)}
                  className={clsx(
                    'flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm transition-colors',
                    index === breadcrumb.length - 1
                      ? 'text-primary-600 dark:text-primary-400 font-medium'
                      : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700'
                  )}
                >
                  {item.type === 'home' && <HomeIcon className="w-4 h-4" />}
                  {item.type === 'project' && <BriefcaseIcon className="w-4 h-4" />}
                  {item.type === 'folder' && <FolderIcon className="w-4 h-4" />}
                  <span className="truncate max-w-[120px]">{item.name}</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Loading content indicator */}
        {isLoadingContent && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Overview View */}
        {viewMode === 'overview' && !isLoadingContent && (
          <div className="space-y-6">
            {/* Documentation Report */}
            {report && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 overflow-hidden"
              >
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
                  <DocumentTextIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span className="font-medium text-gray-900 dark:text-white">{t('portal.statusReport')}</span>
                  <span className="ml-auto text-xs text-gray-500 dark:text-slate-400">
                    {formatShortDate(report.publishedAt)}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {report.title}
                  </h3>
                  <div
                    className="text-sm"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(report.content) }}
                  />
                </div>
              </motion.div>
            )}

            {/* Projects Section */}
            {projects.length > 0 && (
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
                  <BriefcaseIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Proyectos</span>
                  <span className="ml-auto text-xs text-gray-500 dark:text-slate-400">
                    {projects.length} {projects.length === 1 ? 'proyecto' : 'proyectos'}
                  </span>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-slate-700">
                  <AnimatePresence>
                    {projects.map((project, index) => (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleProjectClick(project)}
                        className="flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                      >
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${typeColors[project.type as ProjectType]}15` }}
                        >
                          <BriefcaseIcon
                            className="w-6 h-6"
                            style={{ color: typeColors[project.type as ProjectType] }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-gray-900 dark:text-white truncate">
                              {project.name}
                            </p>
                            <span className={clsx('text-xs px-2 py-0.5 rounded-full flex-shrink-0', statusColors[project.status as ProjectStatus])}>
                              {projectStatusLabels[project.status as ProjectStatus]}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            {projectTypeLabels[project.type as ProjectType]} • {project.folderCount} carpetas • {project.fileCount} archivos
                          </p>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Legacy Shared Files Section */}
            {files.length > 0 && (
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
                  <DocumentIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span className="font-medium text-gray-900 dark:text-white">Archivos Compartidos</span>
                  <span className="ml-auto text-xs text-gray-500 dark:text-slate-400">
                    {files.length} {files.length === 1 ? 'archivo' : 'archivos'}
                  </span>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-slate-700">
                  <AnimatePresence>
                    {files.map((file, index) => (
                      <motion.div
                        key={file.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-slate-700/50 transition-colors"
                      >
                        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                          <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {file.displayName || file.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            {formatBytes(file.size)} • {t('portal.updated', { date: formatShortDate(file.updatedAt) })}
                          </p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDownloadFile(file)}
                          disabled={downloadingFileId === file.id}
                          className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 flex-shrink-0"
                        >
                          {downloadingFileId === file.id ? (
                            <Spinner size="sm" className="text-white" />
                          ) : (
                            <ArrowDownTrayIcon className="w-5 h-5" />
                          )}
                        </motion.button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Empty State */}
            {projects.length === 0 && files.length === 0 && !report && (
              <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-12 text-center">
                <DocumentIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-slate-400">{t('portal.noFiles')}</p>
              </div>
            )}
          </div>
        )}

        {/* Project View (shows folders) */}
        {viewMode === 'project' && selectedProject && !isLoadingContent && (
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 overflow-hidden">
            {/* Project Header */}
            <div className="px-4 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${typeColors[selectedProject.type as ProjectType]}15` }}
                >
                  <BriefcaseIcon
                    className="w-5 h-5"
                    style={{ color: typeColors[selectedProject.type as ProjectType] }}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-semibold text-gray-900 dark:text-white">{selectedProject.name}</h2>
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full', statusColors[selectedProject.status as ProjectStatus])}>
                      {projectStatusLabels[selectedProject.status as ProjectStatus]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {projectTypeLabels[selectedProject.type as ProjectType]}
                  </p>
                </div>
              </div>
            </div>

            {/* Folders List */}
            {folders.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    onClick={() => handleFolderClick(folder)}
                    className="flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FolderIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">Carpeta</p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <FolderIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-slate-400">Este proyecto no tiene carpetas</p>
              </div>
            )}
          </div>
        )}

        {/* Folder View (shows subfolders and files) */}
        {viewMode === 'folder' && selectedProject && !isLoadingContent && (
          <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 overflow-hidden">
            {/* Subfolders */}
            {folders.length > 0 && (
              <div className="divide-y divide-gray-100 dark:divide-slate-700">
                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    onClick={() => handleFolderClick(folder)}
                    className="flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FolderIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{folder.name}</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">Carpeta</p>
                    </div>
                    <ChevronRightIcon className="w-5 h-5 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}

            {/* Files in folder */}
            {folderFiles.length > 0 && (
              <div className={clsx('divide-y divide-gray-100 dark:divide-slate-700', folders.length > 0 && 'border-t border-gray-200 dark:border-slate-600')}>
                {folderFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-4 p-4 hover:bg-gray-50/50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                      <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {formatBytes(file.size)} • {formatShortDate(file.updatedAt)}
                      </p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDownloadProjectFile(file)}
                      disabled={downloadingFileId === file.id}
                      className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {downloadingFileId === file.id ? (
                        <Spinner size="sm" className="text-white" />
                      ) : (
                        <ArrowDownTrayIcon className="w-5 h-5" />
                      )}
                    </motion.button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {folders.length === 0 && folderFiles.length === 0 && (
              <div className="p-12 text-center">
                <DocumentIcon className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-slate-400">Esta carpeta está vacía</p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 dark:text-slate-500 mt-6">
          {t('portal.poweredBy')}
        </p>
      </motion.div>
    </div>
  );
}
